import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Star } from 'lucide-react';
import { useStore } from '../context/StoreContext';
import { useLocale } from '../context/LocaleContext';
import { cdnImage, cdnSrc } from '../utils/cdnImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const absMediaUrl = (u) => {
  if (!u) return u;
  if (u.startsWith('http')) return u;
  return `${BACKEND_URL}${u}`;
};

// Front = primary image; Back = second gallery image when it differs from the front.
export const productImageFront = (p) => (p?.images && p.images[0]) || p?.image;
export const productImageBack = (p) => {
  const imgs = p?.images || [];
  const front = productImageFront(p);
  const back = imgs.find((u) => u && u !== front);
  return back || null;
};

// Reel that only plays when scrolled into view and pauses when out.
// Uses IntersectionObserver so 100 cards on a listing page don't all play at once.
const ReelVideo = ({ src, poster }) => {
  const ref = useRef(null);
  const wantsPlayRef = useRef(false);
  const [broken, setBroken] = React.useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || broken) return;

    const tryPlay = () => {
      if (!wantsPlayRef.current) return;
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };

    const onCanPlay = () => tryPlay();
    el.addEventListener('canplay', onCanPlay);
    el.addEventListener('loadeddata', onCanPlay);

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            wantsPlayRef.current = true;
            if (!el.src) {
              el.src = src;
              el.load();
            }
            tryPlay();
          } else {
            wantsPlayRef.current = false;
            if (!el.paused) el.pause();
          }
        });
      },
      { threshold: [0, 0.5, 1] }
    );
    io.observe(el);

    const onVis = () => { if (document.hidden) el.pause(); else tryPlay(); };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      io.disconnect();
      document.removeEventListener('visibilitychange', onVis);
      el.removeEventListener('canplay', onCanPlay);
      el.removeEventListener('loadeddata', onCanPlay);
      try { el.pause(); } catch {}
    };
  }, [src, broken]);

  // If the video can't be played, show the poster image instead of broken media chrome.
  if (broken) {
    return <img src={poster} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />;
  }

  return (
    <video
      ref={ref}
      data-testid="reel-video"
      poster={poster}
      className="w-full h-full object-cover"
      muted
      loop
      playsInline
      preload="none"
      onError={() => setBroken(true)}
    />
  );
};

const ProductCard = ({ product }) => {
  const { toggleWishlist, isWishlisted } = useStore();
  const { productPrice, productDiscount, t, currency } = useLocale();
  const wl = isWishlisted(product.id);
  const firstVideo = product.videos && product.videos.length > 0 ? product.videos[0] : null;

  return (
    <div className="group relative bg-white">
      <Link to={`/product/${product.id}`} className="block">
        <div className="relative overflow-hidden aspect-[3/4] bg-gray-100">
          {firstVideo ? (
            <ReelVideo src={absMediaUrl(firstVideo)} poster={cdnSrc(absMediaUrl(product.image), 640)} />
          ) : (
            <>
              {(() => {
                const primary = cdnImage(absMediaUrl(product.image));
                const hover = cdnImage(absMediaUrl(product.hoverImage || product.image));
                return (
                  <>
                    <img
                      src={primary.src}
                      srcSet={primary.srcSet}
                      sizes={primary.sizes}
                      alt={product.name}
                      className="w-full h-full object-cover transition-opacity duration-500 group-hover:opacity-0"
                      loading="lazy"
                      decoding="async"
                    />
                    <img
                      src={hover.src}
                      srcSet={hover.srcSet}
                      sizes={hover.sizes}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500 scale-105"
                      loading="lazy"
                      decoding="async"
                    />
                  </>
                );
              })()}
            </>
          )}
          {(() => { const disc = productDiscount(product); return disc > 0 && (
            <div className="absolute top-2 left-2 bg-white px-2 py-0.5 text-[10px] font-bold text-[#22c55e] border border-[#22c55e]" data-testid="card-discount-badge">{disc}% {t('off')}</div>
          ); })()}
          {firstVideo && (
            <div className="absolute bottom-2 left-2 bg-black/70 text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-widest">▶ Reel</div>
          )}
          <button onClick={(e) => { e.preventDefault(); toggleWishlist(product); }} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center shadow hover:bg-white transition">
            <Heart size={16} className={wl ? 'fill-[#22c55e] text-[#22c55e]' : 'text-gray-700'} />
          </button>
        </div>
        <div className="pt-3 pb-4 px-1">
          <div className="text-[11px] text-gray-500 uppercase tracking-wide">{product.collection}</div>
          <h3 className="text-[13px] font-semibold text-gray-900 mt-0.5 line-clamp-1">{product.name}</h3>
          <div className="flex items-center gap-1 mt-1">
            <div className="flex items-center gap-0.5 bg-green-600 text-white px-1.5 py-0.5 rounded text-[10px] font-semibold">
              {product.rating} <Star size={10} className="fill-white" />
            </div>
            <span className="text-[11px] text-gray-500">({product.reviews})</span>
          </div>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-[15px] font-bold text-gray-900">{productPrice(product, 'price')}</span>
            {(() => { const disc = productDiscount(product); return disc > 0 && <>
              <span className="text-[12px] text-gray-400 line-through">{productPrice(product, 'mrp')}</span>
              <span className="text-[12px] text-[#22c55e] font-semibold">{disc}% {t('off')}</span>
            </>; })()}
          </div>
          {currency === 'EUR' && (
            <div className="text-[10px] text-gray-500 mt-0.5">incl. VAT</div>
          )}
        </div>
      </Link>
    </div>
  );
};

export default ProductCard;
